import { http, HttpResponse } from 'msw';
import type {
  ListTypeDefinition,
  ListTypeEntry,
  ListTypeEntryCreatePayload,
  ListTypeEntryUpdatePayload,
} from '../../entities/listType/listTypeTypes';
import { listTypeEntryKey } from '../../entities/listType/listTypeEntryKey';

const API = '/o/crm/v1_0';

const voce = (listTypeEntryId: number, key: string, name: string): ListTypeEntry => ({
  listTypeEntryId,
  key,
  name,
  active: true,
});

/**
 * Le picklist: quelle di company del core ci sono sempre, come nel backend (FILO-67: lo stato di
 * follow-up del contatto rapido, stesse key del file della dotazione). Quelle dei moduli abilitati
 * le portano i plugin con `registerMockListTypeDefinitions`.
 */
const listTypeDefinitions: ListTypeDefinition[] = [
  {
    listTypeDefinitionId: 800,
    externalReferenceCode: 'CRM_RAPID_CONTACT_FOLLOWUP_STATUS',
    name: 'Stato follow-up del contatto rapido',
    listTypeEntries: [
      voce(801, 'contattano', 'Ci contatteranno loro'),
      voce(802, 'prevDaFare', 'Preventivo da fare'),
      voce(803, 'prevComplessi', 'Preventivi complessi da fare'),
      voce(804, 'collaborazioni', 'Possibili collaborazioni'),
      voce(805, 'daRichiamare', 'Da richiamare'),
      voce(806, 'contattato', 'Contattato'),
      voce(807, 'nonInteressato', 'Non interessato'),
    ],
  },
];

/**
 * LA PORTA PER I PLUGIN — stessa ragione di `registerMockBusinesses`: gli handler delle picklist
 * stanno nel core e leggono QUESTO array. Un plugin registra qui le picklist della sua dotazione,
 * e il core non ne conosce i nomi.
 *
 * Va chiamata dentro il `getMockHandlers()` del plugin. Una picklist già registrata (stesso ERC)
 * non si duplica.
 */
export function registerMockListTypeDefinitions(definitions: ListTypeDefinition[]): void {
  for (const definition of definitions) {
    if (!listTypeDefinitions.some((d) => d.externalReferenceCode === definition.externalReferenceCode)) {
      listTypeDefinitions.push(definition);
    }
  }
}

let prossimoId = 900;

const trova = (id: number) => {
  for (const listTypeDefinition of listTypeDefinitions) {
    const item = listTypeDefinition.listTypeEntries.find((i) => i.listTypeEntryId === id);
    if (item) return { listTypeDefinition, item };
  }
  return undefined;
};

/** Stessi codici del backend: 400 testo non valido, 404 inesistente. */
export const listTypeHandlers = [
  http.get(`${API}/list-type-definitions`, () =>
    HttpResponse.json({ items: listTypeDefinitions, page: 1, pageSize: listTypeDefinitions.length, totalCount: listTypeDefinitions.length, lastPage: 1 }),
  ),

  http.post(`${API}/list-type-definition/:id/list-type-entries`, async ({ params, request }) => {
    const listTypeDefinition = listTypeDefinitions.find((m) => m.listTypeDefinitionId === Number(params.id));
    if (!listTypeDefinition) return HttpResponse.json({ title: 'La picklist non esiste' }, { status: 404 });
    const { name } = (await request.json()) as ListTypeEntryCreatePayload;
    const testo = name?.trim();
    if (!testo) return HttpResponse.json({ title: 'Il testo è obbligatorio' }, { status: 400 });
    const base = listTypeEntryKey(testo);
    if (!base) {
      return HttpResponse.json(
        { title: `Dal testo «${testo}» non si ricava una voce: servono lettere o cifre` },
        { status: 400 },
      );
    }
    let key = base;
    for (let n = 2; listTypeDefinition.listTypeEntries.some((i) => i.key === key); n++) key = `${base.slice(0, 75 - String(n).length)}${n}`;
    const creata = voce(prossimoId++, key, testo);
    listTypeDefinition.listTypeEntries = [...listTypeDefinition.listTypeEntries, creata].sort((a, b) => a.name.localeCompare(b.name));
    return HttpResponse.json(creata);
  }),

  http.patch(`${API}/list-type-entry/:id`, async ({ params, request }) => {
    const trovata = trova(Number(params.id));
    if (!trovata) return HttpResponse.json({ title: 'La picklist non esiste' }, { status: 404 });
    const body = (await request.json()) as ListTypeEntryUpdatePayload;
    if (body.name !== undefined && !body.name.trim()) {
      return HttpResponse.json({ title: 'Il testo è obbligatorio' }, { status: 400 });
    }
    const aggiornata: ListTypeEntry = {
      ...trovata.item,
      ...(body.name !== undefined ? { name: body.name.trim() } : {}),
      ...(body.active !== undefined ? { active: body.active } : {}),
    };
    trovata.listTypeDefinition.listTypeEntries = trovata.listTypeDefinition.listTypeEntries.map((i) => (i.listTypeEntryId === aggiornata.listTypeEntryId ? aggiornata : i));
    return HttpResponse.json(aggiornata);
  }),
];
